import type { PartDefinition } from "@rogue/rackbuilder"
import type { RackOptions, SquatStandOptions, WallMountOptions, CaveOptions, CTTowerOptions, MassStorageOptions, CameraLimits } from "../RackTypes"
import { PlyoboxBuilder } from "./equipment/plyoboxBuilder"
import { ThreeTierDumbbellBuilder } from "./equipment/threeTierDumbbellBuilder"
import { createCave } from "./cave/createCave"
import { createCTM1 } from "./ctm1/createCTM1"
import { createMonsterMassStorage } from "./storage/createMonsterMassStorage"
import { createMassStorage } from "./storage/createMassStorage"
import { createSquatStand, createHalfRackTwo } from "./squatStand/createSquatStands"
import { createWallMount } from "./wallmount/createWallmount"
import { BenchBuilder } from "./equipment/benchBuilder"
import { createFlatFootRack } from "./rack/flatFoot/createFlatFoot"
import { createHalfRack } from "./rack/halfRack/createHalfRack"
import { createRack } from "./rack/powerRack/createPowerRack"
import { createFunctionalTrainer } from "./rack/functionalTrainer/createFunctionalTrainer"
import type { IRogue, BuyRequest } from "@/js/services/RogueTypes"
import { RACKBUILDER_FTX, RACKBUILDER_FTX_ADDON } from "@/js/stores/productConstants"
import { createHybridRig } from "./rig/hybrid/createHybridRig"
import { createTrapBar } from "./bar/createTrapBar"

export type PartRetval = {config: Partial<RackOptions | SquatStandOptions | WallMountOptions | CaveOptions | CTTowerOptions | MassStorageOptions | {height?: number}>, part: PartDefinition, camera?: CameraLimits}
export function getPartFromBuyRequest(rogue: IRogue, buyRequest: BuyRequest) : PartRetval | undefined {
  const productId = buyRequest.metadata?.groupedProductId || buyRequest.productId
  const product = rogue.getProductById(productId)

  const sku = product?.sku?.toUpperCase() || ''
  const roguebuilderProp = product.roguebuilder?.props?.rackbuilder || {}

  const type = roguebuilderProp.type || ''
  switch (type) {
    case 'ftx':
    case 'ftxAddon':
    case 'rhinoDropinAddon':
    case 'rhinoDropin':
      return createFunctionalTrainer(rogue, buyRequest)

    case 'rack':
      return createRack(rogue, buyRequest)

    case 'cave':
      return createCave(rogue, buyRequest)

    case 'monsterMassStorage':
      return createMonsterMassStorage(rogue, buyRequest)

    case 'massStorage':
      return createMassStorage(rogue, buyRequest)

    case 'halfRack':
      return createHalfRack(rogue, buyRequest)

    case 'wallmount':
      return createWallMount(rogue, buyRequest)

    case 'foamPlyo':
      return new PlyoboxBuilder(rogue, buyRequest).build()

    case 'flatFoot':
      return createFlatFootRack(rogue, buyRequest)

    case 'ctm1':
      return createCTM1(rogue, buyRequest)

    case 'threeTierDumbbell':
      return { config: {}, part: new ThreeTierDumbbellBuilder().build() }

    case 'squatstand':
      return createSquatStand(rogue, buyRequest)

    case 'hr2':
      return createHalfRackTwo(rogue, buyRequest)

    case 'hybridRig':
      return createHybridRig(rogue, buyRequest)

    case 'adjBench3':
      return { part: new BenchBuilder(rogue, buyRequest).buildAdjBench(), config: {} }
        
    case 'adjBench4':
      return { part: new BenchBuilder(rogue, buyRequest).buildAdjustableBenchFour(), config: {} }

    case 'manta':
      return { part: new BenchBuilder(rogue, buyRequest).buildManta(), config: {} }

    case 'tb3':
      return {
        part   : createTrapBar(rogue, buyRequest),
        config : {},
        camera : { zoomRatios: { min: 0.4, max: 1 }, polarDegrees: { min: 0, max: 180 }, defaultZoom: 0.49, angleDegrees: { azimuth: -122, polar: 73 } },
      }

    default:
    // Handle SKU-based or fallback logic
      if (RACKBUILDER_FTX.includes(sku) || RACKBUILDER_FTX_ADDON.includes(sku) ) {
        return createFunctionalTrainer(rogue, buyRequest)
      }

      if (roguebuilderProp.arMesh) {
        return { config: {}, part: { mesh: roguebuilderProp.arMesh, slots: {} } }
      }

      if (roguebuilderProp.scene?.part) {
        return { config: {}, part: roguebuilderProp.scene.part }
      }

      return undefined
  }
}
